import { DOCUMENT, Injectable, inject } from '@angular/core';
import { RmaUrlsService } from '@rma/generic/util/environment';
import { ShortNumberService } from '@rma/generic/util/pipes/short-number';
import { ApiMapMarker, MAP_MARKER_ICON, MapMarkerStatus, MapMarkerType, MapMarkerTypeIcon, RmaMapMarkerConfig } from '@rma/maps/domain';
import { Memoize } from 'typescript-memoize';

@Injectable()
export class MapMarkerService {
  private readonly rmaUrls = inject(RmaUrlsService);
  private readonly shortNumberPipe = inject(ShortNumberService);
  private readonly document = inject(DOCUMENT);

  private readonly MAX_Z_INDEX = 800;

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Memoize({
    expiring: 60000, // 1 minute
    hashFunction: (mapMarker: ApiMapMarker, markerStatus: MapMarkerStatus, markerIndex: number) => {
      return `${JSON.stringify(mapMarker)}:${markerStatus}:${markerIndex}`;
    },
  })
  public transform(mapMarker: ApiMapMarker, markerStatus: MapMarkerStatus, markerIndex = 0): RmaMapMarkerConfig {
    const { markerType, markerCount } = mapMarker;

    if (markerStatus === 'primary') {
      return this.formatMapMarker('maps/primary.svg', this.MAX_Z_INDEX, false);
    }

    const iconPath = this.getIconPath(markerType, markerCount, markerStatus, !!mapMarker.label);
    const zIndex = this.getZIndex(markerStatus, markerIndex, markerCount);
    const className = this.getClassName(markerType, markerStatus, markerCount);
    const label = this.getLabel(markerType, markerCount, mapMarker.label);
    const clickable = markerStatus !== 'active';

    return this.formatMapMarker(iconPath, zIndex, clickable, className, label);
  }

  public getContent(path: string, label?: string, cssClass?: string): HTMLDivElement | HTMLImageElement {
    const imgMarker = this.document.createElement('img');
    imgMarker.src = path;

    if (label || cssClass) {
      const marker = this.document.createElement('div');
      marker.className = 'relative';
      marker.appendChild(imgMarker);

      const labelSpan = this.document.createElement('span');

      if (label) {
        labelSpan.textContent = label;
      }

      marker.appendChild(labelSpan);

      if (cssClass) {
        labelSpan.className = cssClass;
      }

      return marker;
    }

    return imgMarker;
  }

  private getBaseClassName(markerStatus: MapMarkerStatus, markerType: MapMarkerType): string {
    return `marker ${markerType.toLowerCase()} ${markerStatus.toLowerCase()}`;
  }

  private getClassName(markerType: MapMarkerType, markerStatus: MapMarkerStatus, count: number): string {
    const className = this.getBaseClassName(markerStatus, markerType);

    if (markerType === 'Review' && count > 1) {
      return `${className} marker-count ${count > 9 ? 'marker-count-max' : `marker-count-${count}`}`;
    }

    return className;
  }

  private formatMapMarker(iconPath: string, zIndex: number, gmpClickable: boolean, className?: string, label?: string): RmaMapMarkerConfig {
    const icon = this.rmaUrls.imageUrl(iconPath);
    return {
      icon,
      zIndex,
      gmpClickable,
      className,
      label,
      content: this.getContent(icon, label, className),
    };
  }

  private getIconPath(markerType: MapMarkerType, count: number, mapMarkerIconStatus: MapMarkerStatus, hasLabel: boolean): string {
    const iconType = this.getMapMarkerIconType(markerType, count, hasLabel) ?? '';
    return `maps/${iconType}-${mapMarkerIconStatus}.svg`;
  }

  private getMapMarkerIconType(markerType: MapMarkerType, count: number, hasLabel: boolean): MapMarkerTypeIcon {
    const config = MAP_MARKER_ICON[markerType];
    if (markerType !== 'CompletedListing') {
      return config;
    }

    return count > 1 || !hasLabel ? MAP_MARKER_ICON['ActiveListing'] : MAP_MARKER_ICON['CompletedListing'];
  }

  private getZIndex(mapMaperIconStatus: MapMarkerStatus, index: number, count: number): number {
    const zIndexLimit = this.MAX_Z_INDEX - 1;

    switch (mapMaperIconStatus) {
      case 'active':
        return zIndexLimit;
      case 'standard':
        return count > 1 ? zIndexLimit - index : index;
      default:
        return index;
    }
  }

  private getLabel(mapMarkerType: MapMarkerType, markerCount: number, label?: string): string | undefined {
    if (mapMarkerType === 'Review') {
      return undefined;
    }

    if (mapMarkerType === 'CompletedListing') {
      return markerCount > 1 ? markerCount.toString() : this.formatLabel(label);
    }

    if (mapMarkerType === 'ActiveListing') {
      return markerCount > 1 ? markerCount.toString() : undefined;
    }

    return;
  }

  private formatLabel(label: string | undefined): string {
    return label ? this.shortNumberPipe.abbrieviate(+label.replace('$', '').replace(/,/g, ''), 2, true) : '';
  }
}
